/**
 * Headless date-range math for the Plan / Review pages.
 *
 * Both pages share a set of conventions:
 * - **Period** ∈ {monthly, weekly, daily} drives the bucket size.
 * - **Range** is an inclusive [start, end] pair of ISO dates.
 * - Range × Period ≤ 120 buckets (the "zoom-to-fit" ceiling). When
 *   exceeded, the range is shrunk around an anchor date:
 *     - Plan: anchor = today, weight 1/3 past + 2/3 future
 *     - Review: anchor = cycle.asOfDate, same weighting, with the
 *       future side clamped at today (overflow extends past).
 *
 * No DOM, no React. Frontend wiring imports + passes the result to
 * data adapters and pickers.
 */

import {
  addDays,
  differenceInCalendarDays,
  max,
  min,
  parseISO,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { currentTime } from "./current-time";

export type Period = "monthly" | "weekly" | "daily";

export interface PlanRange {
  from: string; // ISO yyyy-mm-dd
  to: string; // ISO yyyy-mm-dd (inclusive)
}

const MAX_PERIODS = 120;

function periodLengthDays(p: Period): number {
  if (p === "weekly") return 7;
  if (p === "daily") return 1;
  return 30; // approx — used only for bucket-count math, not boundaries
}

function bucketStart(d: Date, period: Period): Date {
  if (period === "weekly") return startOfWeek(d, { weekStartsOn: 1 });
  if (period === "daily") return d;
  return startOfMonth(d);
}

/** Count buckets in an inclusive [from, to] span at given period. */
export function bucketCount(range: PlanRange, period: Period): number {
  const start = bucketStart(parseISO(range.from), period);
  const end = bucketStart(parseISO(range.to), period);
  if (period === "monthly") {
    const months =
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth());
    return months + 1;
  }
  const days = differenceInCalendarDays(end, start);
  const step = period === "weekly" ? 7 : 1;
  return Math.floor(days / step) + 1;
}

/** "Around forecast date" — Review-page default range. */
export function aroundForecastDate(asOf: string): PlanRange {
  const asOfDate = parseISO(asOf);
  const today = currentTime();
  const from = addDays(asOfDate, -90);
  const to = min([today, addDays(asOfDate, 90)]);
  return { from: toISODate(from), to: toISODate(to) };
}

/**
 * Zoom an over-large range down to MAX_PERIODS at the given period.
 *
 * Distribution: ~1/3 of buckets before anchor, ~2/3 after. Plan
 * anchors on today; Review anchors on as-of with a clamp at today
 * (overflow grows the past side).
 *
 * Returns the original range unchanged when within budget.
 */
export function zoomToFit(
  range: PlanRange,
  period: Period,
  opts: {
    anchor: string; // ISO date — today for Plan, asOf for Review
    clampMaxTo?: string; // ISO date — Review uses today
  },
): PlanRange {
  if (bucketCount(range, period) <= MAX_PERIODS) return range;

  const step = periodLengthDays(period);
  // Aim for: 1/3 past + 2/3 future from anchor, in periods. Total
  // buckets are inclusive on both ends, so the past + future counts
  // share the anchor bucket — that's why we subtract 1 from total.
  const pastBuckets = Math.floor((MAX_PERIODS - 1) / 3);
  const futureBuckets = MAX_PERIODS - 1 - pastBuckets;

  const anchorDate = parseISO(opts.anchor);
  let from = addDays(anchorDate, -pastBuckets * step);
  let to = addDays(anchorDate, futureBuckets * step);

  if (opts.clampMaxTo) {
    const cap = parseISO(opts.clampMaxTo);
    if (to > cap) {
      const overflow = differenceInCalendarDays(to, cap);
      to = cap;
      from = addDays(from, -overflow); // extend past side
    }
  }

  return { from: toISODate(from), to: toISODate(to) };
}

/** Bucket boundaries (ISO start dates) inside the range, ordered. */
export function bucketDates(range: PlanRange, period: Period): string[] {
  const out: string[] = [];
  let cur = bucketStart(parseISO(range.from), period);
  const end = bucketStart(parseISO(range.to), period);
  while (cur <= end) {
    out.push(toISODate(cur));
    cur = addDays(cur, periodLengthDays(period));
  }
  return out;
}

/** ``Apr 2026 → Mar 2027`` FY range for the fiscal year a date sits in. */
export function fyRange(asOfDate: string): PlanRange {
  const d = parseISO(asOfDate);
  const fy = d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1;
  return {
    from: `${fy}-04-01`,
    to: `${fy + 1}-03-31`,
  };
}

export function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Re-exports for callers that want the constants without re-typing.
export const PERIOD_BUDGET = MAX_PERIODS;

// Tiny date utilities so callers don't have to import date-fns directly.
export { addDays, differenceInCalendarDays, parseISO } from "date-fns";

// Helpful re-export for tests / pickers.
export const _internals = { max, min };
